/* eslint-disable no-param-reassign */
import { isEmpty, isNil } from 'lodash';
import { SecurityModel } from 'api';
import { INVESTMENT_DATE_ALIAS } from 'common/constants/cap-table';
import {
  CASH_DISTRIBUTION_ALIAS,
  CUSTOM_SECURITY_NAME_ALIAS,
  CUSTOM_SECURITY_NAME_ROW_NUMBER,
  INVESTMENT_CAPITAL_ALIAS,
  LOAN_VALUE_ALIAS,
  PROCEEDS_FROM_SOLD_SHARES_ALIAS,
  SECURITY_ALIAS,
  SECURITY_ROW_NUMBER,
  SHARES_ALIAS,
} from 'common/constants/fund-ownership';
import { CONVERTIBLE_NOTES, OPTION, PREFERRED_STOCK as PREFERRED, WARRANT } from 'common/constants/securityTypes';
import { SpreadsheetConfig } from 'components/ScalarSpreadsheet/utilities/SpreadsheetConfig';
import { CashDistributionLedger, SecurityList } from 'pages/CapTable/fund-ownership/components';
import SecurityListValueViewer from 'pages/CapTable/fund-ownership/components/security-list/SecurityListValueViewer';
import SharesLedger, { SharesLedgerProps } from 'pages/CapTable/fund-ownership/components/shares-ledger/SharesLedger';
import { FundOwnershipTableData } from 'pages/CapTable/fund-ownership/models/fund-ownership.models';
import { FundOwnershipCell } from 'pages/CapTable/fund-ownership/types';
import { gridShortDate } from 'utilities/dateFormats';
import isModeledAfterNote from './isModeledAfterNote';
import { LOAN_VALUE } from '../../cap-table/components/convertible-notes-ledger/config/constants';

const conditions = ({
  cell,
  cells,
  tableData,
  sheet,
}: {
  cell: FundOwnershipCell;
  cells: Record<string, FundOwnershipCell>;
  tableData: FundOwnershipTableData;
  sheet: SpreadsheetConfig;
}): FundOwnershipCell => {
  const { alias, columnLegend } = cell;
  const { capTable, isDisabled } = tableData || {};
  const { columns } = sheet;

  let securityInfo = {} as SecurityModel;
  let multipleInvestments: SecurityModel['multiple_investments'] = [];
  let hasMultipleInvestmentDates = false;

  if (!cell.key) {
    return cell;
  }

  if (cells.fundOwnership) {
    cells = cells.fundOwnership as unknown as Record<string, FundOwnershipCell>;
  }

  const { value } = cells[columnLegend! + SECURITY_ROW_NUMBER] ?? {};

  if (capTable?.securities && value) {
    securityInfo = (capTable.securities.find(s => Number(s.id) === Number(value)) ?? {}) as SecurityModel;

    if (securityInfo) {
      multipleInvestments = securityInfo.multiple_investments ?? [];
      hasMultipleInvestmentDates = multipleInvestments.length > 1;
    }
  }

  const resetCellConfig = () => {
    cell.hidden = true;
    cell.readOnly = isDisabled;
    cell.forceComponent = false;
    cell.disableEvents = false;
    cell.component = null;
    cell.dialog = null;
  };

  const isSecurityType = (type: number) => Number(securityInfo.security_type) === type;

  const modeledAfterConvertibleNote = isModeledAfterNote({ securityInfo });

  const currentCellFO = columns.find((fod: { order: number }) => fod.order === cell.columnOrder);
  const currentCustomSecurityName = currentCellFO?.custom_security_name;
  const isCustomSecurity = !isNil(currentCustomSecurityName) && currentCellFO?.is_custom_security;

  const investmentDates = () => {
    if (!isEmpty(securityInfo) && isSecurityType(PREFERRED)) {
      if (hasMultipleInvestmentDates) {
        return multipleInvestments.map((investment: SecurityModel['multiple_investments']) => investment.date);
      }

      if (securityInfo.investment_date) {
        return gridShortDate(securityInfo.investment_date);
      }
    }

    return null;
  };

  const getSharesLedgerProps = (includeDates = true): SharesLedgerProps => {
    cell.relatedSecurity = securityInfo.id;
    cell.isCustomSecurity = isCustomSecurity;
    if (modeledAfterConvertibleNote) {
      cell.modeledAfterConvertibleNote = modeledAfterConvertibleNote;
    } else if (includeDates) {
      cell.investmentDates = investmentDates();
    }
    return {} as SharesLedgerProps;
  };

  const defaultSharesLedgerProps = () => {
    cell.disableBackdropClick = false;
  };

  const isLoanSecurity = () => {
    const equityModel = (capTable?.securities ?? [])
      .find(({ security_ref }) => security_ref === securityInfo.security_group_ref)
      ?.convertible_notes?.find(
        ({ security_group_ref }) => security_group_ref === securityInfo.security_ref
      )?.model_as_equity;
    return equityModel === LOAN_VALUE;
  };

  const sharesValueProps = () => {
    cell.readOnly = isDisabled;
    cell.dialog = {
      content: <SharesLedger {...getSharesLedgerProps()} />,
      maxWidth: 'lg',
    };
    defaultSharesLedgerProps();
  };

  // 02. security
  if (alias === SECURITY_ALIAS) {
    if (isCustomSecurity) {
      // Is Custom Security
      cell.gridType = 'string';
      cell.dbType = 'string';
      cell.placeholder = 'ENTER DATA';
      cell.dataEditor = null;
      cell.valueViewer = null;
      const customSecurityName = cell.value ?? '';
      const customSecurityNameCellKey = `${cell.columnLegend}${CUSTOM_SECURITY_NAME_ROW_NUMBER}`;
      cells[customSecurityNameCellKey].value = customSecurityName;
    } else {
      cell.defaultValue = null;
      cell.gridType = 'number';
      cell.dbType = 'number';
      cell.dataEditor = (props: any) => <SecurityList {...props} />;
      cell.valueViewer = (props: any) => <SecurityListValueViewer {...props} />;
    }
  }

  // 04. Investment Date
  if (alias === INVESTMENT_DATE_ALIAS) {
    resetCellConfig();
    cell.hidden = false;
    cell.disableEvents = true;
    cell.isRequired = false;
    cell.dialog = {
      content: <SharesLedger {...getSharesLedgerProps(false)} />,
      maxWidth: 'lg',
    };

    if (!isEmpty(securityInfo) && isSecurityType(PREFERRED)) {
      if (securityInfo.has_dividends) {
        if (hasMultipleInvestmentDates) {
          // Set security investment dates as dropdown
          cell.dialog = {
            content: <SharesLedger {...getSharesLedgerProps()} />,
            maxWidth: 'lg',
          };
        } else {
          // Set security investment date as readonly
          cell.readOnly = isDisabled;
          cell.value = investmentDates();
          cell.dialog = {
            content: <SharesLedger {...getSharesLedgerProps()} />,
            maxWidth: 'lg',
          };
        }
      }
    }

    defaultSharesLedgerProps();
  }

  // 05. Investment Capital, 06. Shares, 12. Proceeds from old shares
  if ([INVESTMENT_CAPITAL_ALIAS, SHARES_ALIAS, PROCEEDS_FROM_SOLD_SHARES_ALIAS].includes(alias)) {
    sharesValueProps();
  }

  if (alias === INVESTMENT_CAPITAL_ALIAS) {
    cell.greaterThan = 0;
    if ((isSecurityType(CONVERTIBLE_NOTES) && isLoanSecurity()) || isSecurityType(WARRANT) || isSecurityType(OPTION)) {
      cell.greaterThan = undefined;
    }
  }

  if (alias === LOAN_VALUE_ALIAS && isLoanSecurity()) {
    sharesValueProps();
  }

  // 11.  Cash Distributions
  if (alias === CASH_DISTRIBUTION_ALIAS) {
    cell.hidden = false;
    cell.readOnly = isDisabled;
    cell.dialog = {
      content: <CashDistributionLedger />,
    };
  }

  // 15.  Custom Security Name
  if (alias === CUSTOM_SECURITY_NAME_ALIAS) {
    if (!isCustomSecurity) {
      cell.value = '';
    }
  }
  return cell;
};

export default conditions;
